import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Flame, Gift, ArrowRight, Lock, Sparkles, History, Settings as SettingsIcon, Award, Check, Users, Compass } from "lucide-react";
import { Logo } from "../components/Logo";
import { themeClass } from "../components/ThemeChip";
import { toast } from "sonner";

function Greeting({ name }) {
  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return (
    <div>
      <div className="text-sm text-muted-foreground tracking-wide uppercase">{greet}</div>
      <h1 className="font-display text-4xl">{name || "there"}</h1>
    </div>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { me, logout, refresh } = useAuth();
  const [progress, setProgress] = useState(null);
  const [streak, setStreak] = useState({ current: 0, best: 0 });
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [themes, setThemes] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [children, setChildren] = useState([]);
  const [billing, setBilling] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [journey, setJourney] = useState(null);

  const loadAll = async () => {
    try {
      const [p, s, w, h, t, m, c, b, j] = await Promise.all([
        api.get("/curriculum/progress"),
        api.get("/streak"),
        api.get("/wishes"),
        api.get("/session/history"),
        api.get("/curriculum/themes"),
        api.get("/milestones"),
        api.get("/profile/children"),
        api.get("/billing/status"),
        api.get("/session/journey/state").catch(() => ({ data: null })),
      ]);
      setProgress(p.data);
      setStreak(s.data);
      setBalance(w.data.balance || 0);
      setHistory(h.data.slice(0, 3));
      setThemes(t.data);
      setMilestones(m.data);
      setChildren(c.data.children || []);
      setBilling(b.data);
      setJourney(j.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const switchChild = async (id) => {
    if (id === me?.child?.id || switching) return;
    setSwitching(true);
    try {
      await api.post(`/profile/active-child/${id}`);
      await refresh();
      await loadAll();
      const newKid = children.find((c) => c.id === id);
      if (newKid) toast.success(`Now showing ${newKid.name}'s journey`);
    } catch { toast.error("Could not switch child"); }
    finally { setSwitching(false); }
  };

  if (!me?.child) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }

  const current = progress?.current;
  const nextWeek = progress?.next_week;
  const lastCompleted = progress?.last_completed;
  const lockedThemes = themes.filter((t) => t.status === "coming_soon");

  return (
    <div className="min-h-screen pb-24" data-testid="home-screen">
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="flex items-center justify-between mb-7">
          <Logo size={102} />
          <div className="flex items-center gap-2">
            <Link to="/settings" className="p-2 rounded-full hover:bg-white/70" data-testid="home-settings-btn">
              <SettingsIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>

        <Greeting name={me?.profile?.name?.split(" ")[0]} />

        {/* Active child banner + switcher */}
        <div className="mt-4 mb-1" data-testid="home-child-switcher">
          <div className="card-soft p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Users className="w-3 h-3"/> This week's session is for
              </div>
              {children.length > 1 && (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Tap to switch</span>
              )}
            </div>
            <div className="font-display text-2xl text-[hsl(var(--neu-orange))] mb-1"
                 data-testid="home-active-child-name">
              {me?.child?.name} <span className="text-base text-muted-foreground">· age {me?.child?.age_group}</span>
            </div>
            {children.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {children.map((c) => {
                  const active = c.id === me?.child?.id;
                  return (
                    <button key={c.id} onClick={() => switchChild(c.id)}
                            disabled={active || switching}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? "bg-[hsl(var(--neu-orange))] text-white border-[hsl(var(--neu-orange))] cursor-default"
                                : "bg-white/80 hover:bg-white text-[hsl(var(--neu-ink))] border-[hsl(var(--neu-border))]"
                            }`}
                            data-testid={`home-child-chip-${c.id}`}>
                      {active && <Check className="inline w-3 h-3 mr-1 -mt-0.5"/>}
                      {c.name} <span className="opacity-70">· {c.age_group}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Streak & Points chips */}
        <div className="flex flex-wrap gap-2 mt-5">
          <Link to="/streak" className="pill" data-testid="home-streak-chip">
            <Flame className="w-4 h-4 text-[hsl(var(--neu-orange))]" />
            <span><b>{streak.current}</b> week streak</span>
          </Link>
          <Link to="/wishes" className="pill" data-testid="home-wishes-chip">
            <Gift className="w-4 h-4 text-[hsl(var(--neu-pink))]" />
            <span><b>{balance}</b> points</span>
          </Link>
          {milestones.length > 0 && (
            <Link to="/themes" className="pill" data-testid="home-milestones-chip">
              <Award className="w-4 h-4 text-[hsl(var(--neu-orange))]" />
              <b>{milestones.length}</b> badges
            </Link>
          )}
        </div>

        {/* Programs bar — clear separation between the two NeuroEQ programs */}
        <ProgramsBar billing={billing} />

        {/* Subscription / trial status */}
        <BillingChip billing={billing} activeChildId={me?.child?.id} />

        {/* Journey (Before ↔ After) banner */}
        <JourneyBanner journey={journey} onOpen={() => nav("/journey")} />

        {/* Current week card */}
        <div className="mt-7">
          {current ? (
            <CurrentWeekCard current={current} childName={me.child.name}
                              onStart={() => nav("/session")} />
          ) : (
            <WeekDoneCard lastCompleted={lastCompleted} nextWeek={nextWeek} />
          )}
        </div>

        {/* Previous sessions */}
        {history.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl">Previous sessions</h3>
              <Link to="/history" className="text-sm text-[hsl(var(--neu-orange))]" data-testid="home-history-link">
                See all <History className="inline w-3 h-3 ml-1"/>
              </Link>
            </div>
            <div className="space-y-3">
              {history.map((h) => (
                <Link key={h.session.id} to={`/history/${h.session.id}`}
                      className={`card-soft p-4 flex items-center gap-4 ${themeClass(h.theme?.color_token)}`}
                      data-testid={`home-history-${h.session.id}`}>
                  <div className="w-11 h-11 rounded-2xl theme-surface theme-text flex items-center justify-center font-display text-xl">
                    {h.week?.week_number}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider theme-text">{h.theme?.title}</div>
                    <div className="font-medium">{h.week?.title}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground"/>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Locked future themes */}
        {lockedThemes.length > 0 && (
          <div className="mt-9">
            <h3 className="font-display text-2xl mb-3">Coming soon</h3>
            <div className="flex gap-3 overflow-x-auto pb-3 -mx-6 px-6">
              {lockedThemes.map((t) => (
                <div key={t.id} className="card-soft p-4 shrink-0 w-44 opacity-80" data-testid={`home-locked-${t.slug}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Lock className="w-3 h-3"/> Locked
                  </div>
                  <div className="font-display text-xl mt-2">{t.title}</div>
                  <div className="text-xs text-muted-foreground">{t.subtitle}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 mb-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="w-3 h-3 text-[hsl(var(--neu-orange))]"/>
          A weekly family ritual, never an app to scroll
        </div>
      </div>
    </div>
  );
}

function CurrentWeekCard({ current, childName, onStart }) {
  return (
    <div className={`card-elevated relative overflow-hidden ${themeClass(current.theme_color)}`}
         data-testid="home-current-week-card">
      <div className="absolute inset-0 theme-surface opacity-60" />
      <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full theme-accent-bg opacity-10"/>
      <div className="relative p-7">
        <div className="flex items-center justify-between mb-2">
          <span className="chip theme-surface theme-text">Week {current.week_number}</span>
          <span className="text-xs uppercase tracking-widest text-muted-foreground">This Sunday · 30 min</span>
        </div>
        <div className="text-sm uppercase tracking-widest theme-text mb-1">{current.theme_title}</div>
        <h2 className="font-display text-3xl leading-tight">{current.title}</h2>
        <p className="text-muted-foreground mt-2">
          Ready for you and <span className="font-medium text-[hsl(var(--neu-ink))]">{childName}</span>.
        </p>

        <button onClick={onStart}
                className="btn-dark w-full mt-6 text-lg" data-testid="home-start-session-btn">
          Start this week's session <ArrowRight className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}

function WeekDoneCard({ lastCompleted, nextWeek }) {
  // No previous completed session at all → first-time empty state.
  if (!lastCompleted) {
    return (
      <div className="card-soft p-7 text-center" data-testid="home-all-caught-up">
        <Sparkles className="w-8 h-8 text-[hsl(var(--neu-orange))] mx-auto mb-3"/>
        <h2 className="font-display text-2xl">You're all caught up</h2>
        <p className="text-muted-foreground mt-2">
          Your next week unlocks on Sunday. We'll be here.
        </p>
      </div>
    );
  }
  // Compute a friendly "next Sunday" label.
  const nextSundayLabel = (() => {
    if (nextWeek?.unlocks_on) {
      try {
        const d = new Date(nextWeek.unlocks_on);
        return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
      } catch { /* fall through */ }
    }
    return "this coming Sunday";
  })();
  return (
    <div className={`card-elevated relative overflow-hidden ${themeClass(lastCompleted.theme_color)}`}
         data-testid="home-week-done-card">
      <div className="absolute inset-0 theme-surface opacity-60" />
      <div className="relative p-7">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--neu-orange))]/15 flex items-center justify-center">
            <Check className="w-5 h-5 text-[hsl(var(--neu-orange))]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              This week's session is done
            </div>
            <div className="text-sm text-muted-foreground">{lastCompleted.theme_title} · Week {lastCompleted.week_number}</div>
          </div>
        </div>
        <h2 className="font-display text-2xl leading-tight mt-2">{lastCompleted.title}</h2>

        {nextWeek && (
          <div className="mt-6 pt-5 border-t border-black/5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Coming up next</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-lg">{nextWeek.title}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {nextWeek.theme_title} · Week {nextWeek.week_number} · Unlocks {nextSundayLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BillingChip({ billing, activeChildId }) {
  if (!billing || !activeChildId) return null;
  const me = billing.children.find((c) => c.child_id === activeChildId);
  if (!me) return null;
  const a = me.access;
  if (a.reason === "subscribed") {
    const d = a.valid_until ? new Date(a.valid_until) : null;
    const dateLabel = d ? d.toLocaleDateString(undefined,
      { month: "short", day: "numeric", year: "numeric" }) : "";
    return (
      <Link to="/settings" className="block mt-5 px-4 py-2.5 rounded-full bg-[hsl(var(--neu-green))]/10 text-[hsl(var(--neu-green))] text-xs flex items-center justify-between"
            data-testid="home-billing-active">
        <span className="font-medium uppercase tracking-widest">Plan active</span>
        <span>until {dateLabel}</span>
      </Link>
    );
  }
  if (a.reason === "grandfathered") {
    return (
      <div className="mt-5 px-4 py-2.5 rounded-full bg-[hsl(var(--neu-blue))]/10 text-[hsl(var(--neu-blue))] text-xs flex items-center justify-between"
           data-testid="home-billing-grandfathered">
        <span className="font-medium uppercase tracking-widest">Pilot access</span>
        <span>Free during launch period</span>
      </div>
    );
  }
  if (a.reason === "trial") {
    const left = a.trial_sessions_left ?? 0;
    return (
      <Link to="/paywall" className="block mt-5 px-4 py-2.5 rounded-full bg-[hsl(var(--neu-orange))]/10 text-[hsl(var(--neu-orange))] text-xs flex items-center justify-between"
            data-testid="home-billing-trial">
        <span className="font-medium uppercase tracking-widest">Free trial</span>
        <span>{left} of {a.trial_sessions_total} session{left === 1 ? "" : "s"} left · See plans →</span>
      </Link>
    );
  }
  if (a.reason === "paywall") {
    return (
      <Link to="/paywall" className="block mt-5 px-4 py-2.5 rounded-full bg-[hsl(var(--neu-orange))] text-white text-xs flex items-center justify-between"
            data-testid="home-billing-paywall">
        <span className="font-medium uppercase tracking-widest">Trial complete</span>
        <span>Choose a plan to continue →</span>
      </Link>
    );
  }
  return null;
}


function JourneyBanner({ journey, onOpen }) {
  if (!journey || !journey.stage || journey.stage === "none" ||
      journey.stage === "unavailable" || journey.stage === "in_progress") {
    return null;
  }
  const themeTitle = journey.theme?.title || "this month";
  const map = {
    baseline_required: {
      label: "Baseline check-in",
      hint: `Before you start ${themeTitle}, capture your child's baseline (3 quick scenarios).`,
      cta: "Start baseline",
      tone: "bg-[hsl(var(--neu-orange))] text-white",
    },
    final_required: {
      label: "Final check-in",
      hint: `You've finished ${themeTitle}. Ask the same 3 scenarios to see how thinking has shifted.`,
      cta: "Do final check-in",
      tone: "bg-[hsl(var(--neu-orange))] text-white",
    },
    reflection_required: {
      label: "Compare before ↔ after",
      hint: `Both check-ins are done. Take a moment to compare and reflect.`,
      cta: "Open reflection",
      tone: "bg-[hsl(var(--neu-blue))] text-white",
    },
    summary: {
      label: "Journey summary",
      hint: `See how your child's thinking has grown through ${themeTitle}.`,
      cta: "View journey",
      tone: "bg-[hsl(var(--neu-green))] text-white",
    },
  };
  const cfg = map[journey.stage];
  if (!cfg) return null;
  return (
    <button onClick={onOpen}
            className={`mt-5 w-full text-left rounded-3xl p-5 ${cfg.tone} shadow-lg shadow-black/5 flex items-start gap-4 transition-transform active:scale-[0.98]`}
            data-testid={`home-journey-banner-${journey.stage}`}>
      <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
        <Compass className="w-5 h-5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest opacity-90">Before ↔ After</div>
        <div className="font-display text-xl leading-tight">{cfg.label}</div>
        <div className="text-xs opacity-90 mt-1">{cfg.hint}</div>
        <div className="text-xs font-medium mt-2 inline-flex items-center gap-1">
          {cfg.cta} <ArrowRight className="w-3 h-3"/>
        </div>
      </div>
    </button>
  );
}


function ProgramsBar({ billing }) {
  const ents = billing?.entitlements || {};
  const jidnyasaActive = !!ents.jidnyasa;
  const neuroeqActive = !!ents.neuroeq;
  return (
    <div className="mt-6" data-testid="home-programs-bar">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-medium">
        Your NeuroEQ programs
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* JLP · Jidnyasa Learning Program */}
        <Link
          to={jidnyasaActive ? "/jidnyasa/dashboard" : "/jidnyasa"}
          className="group relative rounded-3xl p-5 bg-gradient-to-br from-[hsl(var(--neu-green))] to-[hsl(var(--neu-green-deep))] text-white shadow-lg shadow-[hsl(var(--neu-green))]/25 hover:shadow-xl hover:-translate-y-0.5 transition-all block"
          data-testid="home-program-jlp"
        >
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded-full font-medium">
              JJLP
            </span>
            {jidnyasaActive && (
              <span className="text-[10px] uppercase tracking-widest bg-white/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Check className="w-3 h-3"/> Active
              </span>
            )}
          </div>
          <div className="font-display text-xl mt-2 leading-tight">
            Jidnyasa Jr Leaders Program
          </div>
          <p className="text-xs opacity-90 mt-1.5">
            {jidnyasaActive
              ? "Go to your live weekly class · attend, replays, session kit"
              : "Live 60-min class every week · Knowledge of Self + World · age 7–13"}
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium bg-white text-[hsl(var(--neu-green-deep))] px-4 py-1.5 rounded-full">
            {jidnyasaActive ? "Open JJLP dashboard" : "Explore JJLP"}
            <ArrowRight className="w-3.5 h-3.5"/>
          </div>
        </Link>

        {/* NGP · NeuroEQ Growth Program */}
        <div
          className="rounded-3xl p-5 bg-white border-2 border-[hsl(var(--neu-orange))]/25 relative"
          data-testid="home-program-ngp"
        >
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest bg-[hsl(var(--neu-orange))] text-white px-2 py-0.5 rounded-full font-medium">
              NGP
            </span>
            {neuroeqActive ? (
              <span className="text-[10px] uppercase tracking-widest bg-[hsl(var(--neu-orange))]/10 text-[hsl(var(--neu-orange-deep))] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Check className="w-3 h-3"/> Active
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Where you are now
              </span>
            )}
          </div>
          <div className="font-display text-xl mt-2 leading-tight">
            NeuroEQ Growth Program
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Your weekly Sunday family ritual · 4 steps, 30 minutes together.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            Scroll down for this week&apos;s session
          </div>
        </div>
      </div>
    </div>
  );
}


